import { Link, useNavigate } from 'react-router-dom'

import { Badge } from './ui.tsx'
import { gbp, odds, pnl, pnlClass } from '../lib/format.ts'
import type { PnlBet } from '../../shared/types.ts'

/**
 * A list of bets — singles and accumulators, open and settled — as P&L and a bankroll
 * round both show them. A single opens its market; an accumulator lists its legs.
 */

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function BetsTable({ bets }: { bets: PnlBet[] }) {
  return (
    <div className="table-wrap">
      <table className="tbl rows-clickable">
        <thead>
          <tr>
            <th>Placed</th>
            <th>Round</th>
            <th>Game</th>
            <th>Bet</th>
            <th>Price</th>
            <th>Stake</th>
            <th>Result</th>
            <th className="num">Profit</th>
          </tr>
        </thead>
        <tbody>
          {bets.map((b) => (
            <BetRow key={b.ref} bet={b} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function resultBadge(status: PnlBet['status'] | 'leg', result: PnlBet['result']) {
  if (status === 'open' || (status === 'leg' && result === null)) return <Badge dot>Open</Badge>
  if (result === 'won') return <Badge tone="pos">Won</Badge>
  if (result === 'lost') return <Badge tone="neg">Lost</Badge>
  if (result === 'void') return <Badge tone="skipped">Void</Badge>
  return <Badge tone="skipped">Settled</Badge>
}

function BetRow({ bet: b }: { bet: PnlBet }) {
  const navigate = useNavigate()
  const first = b.legs[0]
  const single = b.kind === 'single' && first
  const open = () => first && navigate(`/markets/${first.marketId}`)

  return (
    <tr
      onClick={single ? open : undefined}
      onKeyDown={(e) => {
        if (single && e.key === 'Enter') open()
      }}
      tabIndex={single ? 0 : undefined}
      role={single ? 'link' : undefined}
      aria-label={single ? `Open ${first.event}` : undefined}
    >
      <td className="u-mono u-sm">{when(b.placedAt)}</td>
      <td className="u-mono u-sm">{b.round ?? '—'}</td>
      <td>
        {single ? (
          <>
            <div className="cell-strong">{first.event}</div>
            <div className="cell-sub">{[first.marketType, first.competition].filter(Boolean).join(' · ')}</div>
          </>
        ) : (
          <div className="cell-strong">{b.legs.length}-fold accumulator</div>
        )}
      </td>
      <td>
        {single ? (
          first.runner
        ) : (
          <ul className="acc-legs">
            {b.legs.map((l, i) => (
              <li key={i}>
                <Link to={`/markets/${l.marketId}`}>{l.runner}</Link>{' '}
                <span className="u-mono">@ {odds(l.price)}</span>{' '}
                <span className="cell-sub">{[l.event, l.marketType, l.score].filter(Boolean).join(' · ')}</span>{' '}
                {resultBadge('leg', l.result)}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="u-mono">{odds(b.price)}</td>
      <td className="u-mono">{gbp(b.stake)}</td>
      <td>
        {resultBadge(b.status, b.result)}
        {single && first.score && <span className="cell-sub u-mono"> {first.score}</span>}
      </td>
      <td className={`num u-mono ${pnlClass(b.profit)}`}>{b.profit === null ? '—' : pnl(b.profit)}</td>
    </tr>
  )
}
